import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import { useApi } from "../../../../../context/api.js";
import MDButton from "../../../../../components/MDButton";
import MDTypography from "../../../../../components/MDTypography";
import { formatDate } from "../../../../../utils/date.js";
import MDBox from "../../../../../components/MDBox";
import DocumentIcon from "@mui/icons-material/Description";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import { setMilestone, useHomework } from "../../../../../context/homework";

function ManualValidation({ m, from, title, proofs = false, min = 0, max = 5, onCancel }) {
  const api = useApi();
  const [_, dispatch] = useHomework();
  const [loading, setLoading] = useState(0);
  const [userProofs, setUserProofs] = useState(null);
  const [fullSize, setFullSize] = useState(null);

  useEffect(() => {
    if (proofs) {
      api.getMilestoneProofs(m.id, from).then((res) => {
        setUserProofs(res || []);
      });
    }
  }, [m.id, from, proofs]);

  const handleProofClick = (proof) => {
    if (proof.type.startsWith("image/")) {
      // Display file in full size
      setFullSize(proof.id);
    } else {
      // TODO : Audio/Video player ?
      // Download file
      const link = document.createElement("a");
      link.href = "/files/proofs/" + proof.id;
      link.download = proof.id;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleReject = () => {
    setLoading(2);
    api
      .rejectMilestone(m.id, from)
      .then((res) => {
        setLoading(0);
        if (res) {
          setMilestone(dispatch, m.id, from);
          onCancel();
        } else {
          // TODO : Show error ?
        }
      })
      .catch((err) => {
        setLoading(false);
        // TODO : Show error ?
      });
  };
  const handleAccept = () => {
    setLoading(1);
    api
      .validateMilestone(m.id, from)
      .then((res) => {
        setLoading(0);
        if (res) {
          setMilestone(dispatch, m.id, from, null);
          onCancel();
        } else {
          // TODO : Show error ?
        }
      })
      .catch((err) => {
        setLoading(false);
        // TODO : Show error ?
      });
  };

  return (
    <>
      <DialogContent>
        {title}
        <MDTypography variant="body" component="div" mt={2}>
          {m.submitted
            ? "Votre enfant vous a notifié avoir terminé cette étape le " +
              formatDate(m.submission_date) +
              "."
            : "Vous êtes sur le point de valider cette étape de votre enfant."}
        </MDTypography>
        {proofs && (
          <>
            {!userProofs && (
              <MDBox display="flex" justifyContent="center">
                <CircularProgress color="info" />
              </MDBox>
            )}
            {!!(userProofs && userProofs.length) && (
              <>
                <MDTypography variant="body" component="div" mt={2}>
                  Voici les preuves qu&apos;il a fournies :
                </MDTypography>
                <MDBox display="flex" alignItems="center" flexWrap="wrap">
                  {userProofs.map((proof) => (
                    <MDBox
                      key={proof.id}
                      style={{
                        border: "1px solid silver",
                        padding: 16,
                        borderRadius: 8,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        position: "relative",
                        minWidth: 120,
                        maxWidth: 240,
                        margin: 8,
                        cursor: "zoom-in",
                      }}
                      onClick={() => handleProofClick(proof)}
                    >
                      {proof.type.startsWith("image/") && (
                        <MDBox
                          component="span"
                          sx={{
                            height: 64,
                            width: "100%",
                            position: "relative",
                            mb: 1,
                          }}
                        >
                          <img
                            src={"/files/proofs/" + proof.id}
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              margin: "auto",
                              maxWidth: "100%",
                              maxHeight: "100%",
                              borderRadius: "4px",
                              overflow: "hidden",
                            }}
                          />
                        </MDBox>
                      )}
                      {proof.type.startsWith("audio/") && (
                        <AudioFileIcon sx={{ width: 64, height: 64, mb: 1 }} />
                      )}
                      {proof.type.startsWith("video/") && (
                        <VideoFileIcon sx={{ width: 64, height: 64, mb: 1 }} />
                      )}
                      {!proof.type.startsWith("image/") &&
                        !proof.type.startsWith("audio/") &&
                        !proof.type.startsWith("video/") && (
                          <DocumentIcon sx={{ width: 64, height: 64, mb: 1 }} />
                        )}
                      <MDTypography
                        component="div"
                        variant="caption"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          width: "100%",
                          textAlign: "center",
                        }}
                      >
                        {formatDate(proof.created)}
                      </MDTypography>
                    </MDBox>
                  ))}
                </MDBox>
              </>
            )}
          </>
        )}
        {!proofs && (
          <MDTypography variant="body" component="div" mt={2}>
            Cette étape ne nécessitait pas de preuves.
          </MDTypography>
        )}
      </DialogContent>
      <DialogActions>
        {m.submitted && (
          <MDButton variant="contained" color="error" disabled={!!loading} onClick={handleReject}>
            {loading === 2 && <CircularProgress size={24} />}
            {loading !== 2 && "Refuser son étape"}
          </MDButton>
        )}
        <MDButton variant="contained" color="success" disabled={!!loading} onClick={handleAccept}>
          {loading === 1 && <CircularProgress size={24} />}
          {loading !== 1 && "Valider son étape"}
        </MDButton>
      </DialogActions>
      {fullSize && (
        <Dialog open={!!fullSize} maxWidth="xl" fullWidth onClose={() => setFullSize(null)}>
          {fullSize && <img src={"/files/proofs/" + fullSize} />}
        </Dialog>
      )}
    </>
  );
}

ManualValidation.propTypes = {
  m: PropTypes.object.isRequired,
  from: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  title: PropTypes.element,
  proofs: PropTypes.bool,
  min: PropTypes.number,
  max: PropTypes.number,
};

export default ManualValidation;
